import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useCallback, useMemo } from "react";
import { ArrowRight, Clock4 } from "lucide-react";
import {
  APPOINTMENT_BUFFER_MINUTES,
  isTooSoon,
  tripWallClockToMs,
  type SavedPhone,
  type SavedPickup,
} from "@mtform/shared";
import { Button } from "@/components/ui/button";
import { ChipRadioGroup } from "@/components/ui/chip-radio";
import { Field } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { TimeField } from "@/components/form/TimeField";
import { PhoneField } from "@/components/form/PhoneField";
import { AddressField } from "@/components/form/AddressField";
import { AddressPickHint } from "@/components/form/AddressPickHint";
import { AddressConfirmedHint } from "@/components/form/AddressConfirmedHint";
import { LocationsPicker } from "@/components/form/LocationsPicker";
import { SavedValuePicker } from "@/components/form/SavedValuePicker";
import { WillCallToggle } from "@/components/form/WillCallToggle";
import {
  formatTimeForDisplay,
  formatUsPhone,
  fullAddressFromPlace,
  joinAddressLine,
  needsAmPm,
  normalizeDateForPayload,
  normalizeTimeForPayload,
  shiftTimeForDisplay,
} from "@/lib/format";
import { Input } from "@/components/ui/input";
import { WizardCard } from "./WizardCard";
import { usePatientLookupState } from "./PatientLookupContext";
import type { TripFormValues } from "./schema";
import type { WizardStepStatus } from "./WizardCard";

interface PickupSectionProps {
  index: number;
  status: WizardStepStatus;
  onContinue: () => void;
  onEdit: () => void;
}

// APPOINTMENT_BUFFER_MINUTES now comes from @mtform/shared (leadTime.ts). It
// used to be a local 15 here, a second local 15 in schema.ts, and a third in
// the lead-time rule - three copies of one contracted number. They must agree:
// the buffer decides both the pickup time shown here AND how much extra notice
// an appointment-mode booking needs, so a change to one and not the others
// would quietly mis-state one of them.

// =============================================================================
// PickupSection
//
// Wizard step covering everything between "where do we pick the patient
// up" and "what time". The transport date moved to its own earlier step
// so this card stays focused.
//
// Address UX:
//   - Staff pick the address from the Google dropdown (or a curated Mount
//     Sinai location / saved pick-up), which gives us exact lat/lon plus the
//     city/state/zip — shown as a small muted "Confirmed" line under the
//     address. There is no manual City/State/ZIP input.
//   - When an address has no coordinates yet (typed but not picked), a
//     gentle, non-blocking AddressPickHint nudges staff to choose from the
//     dropdown. The server geocodes as a safety net, so it never blocks the
//     booking.
// =============================================================================

export function PickupSection({
  index,
  status,
  onContinue,
  onEdit,
}: PickupSectionProps) {
  const {
    control,
    register,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext<TripFormValues>();

  const pickupAddress = useWatch({ control, name: "pickupAddress" }) ?? "";
  const pickupCityZip = useWatch({ control, name: "pickupCityZip" }) ?? "";
  const pickupPlace = useWatch({ control, name: "pickupPlace" });
  const pickupWillCall = useWatch({ control, name: "pickupWillCall" });
  const timeType = useWatch({ control, name: "timeType" });
  const primaryTime = useWatch({ control, name: "primaryTime" }) ?? "";
  const contactPhoneWatch = useWatch({ control, name: "contactPhone" }) ?? "";

  const arrivalTimeHint = useMemo(() => {
    // Nothing to work out from a time still to be told AM or PM (needsAmPm).
    if (pickupWillCall || timeType !== "appointment" || !primaryTime || needsAmPm(primaryTime)) return "";
    return shiftTimeForDisplay(primaryTime, APPOINTMENT_BUFFER_MINUTES);
  }, [pickupWillCall, timeType, primaryTime]);

  const showAppointmentBuffer =
    !pickupWillCall && timeType === "appointment" && Boolean(arrivalTimeHint);

  // Reveal the short-notice reason only when it is actually needed: the user
  // may override the minimum lead time AND the time they have chosen is inside
  // it. Recomputed from the watched date/time, so it appears the moment they
  // pick something too soon and disappears if they pick a later slot.
  const transportDate = useWatch({ control, name: "transportDate" }) ?? "";
  const canBookShortNotice =
    useWatch({ control, name: "canBookShortNotice" }) ?? false;
  const showShortNoticeReason = useMemo(() => {
    if (!canBookShortNotice || pickupWillCall || needsAmPm(primaryTime)) return false;
    const atMs = tripWallClockToMs(
      normalizeDateForPayload(transportDate),
      normalizeTimeForPayload(primaryTime),
    );
    if (atMs == null) return false;
    return isTooSoon(
      atMs,
      Date.now(),
      timeType === "appointment" ? "appointment" : "pickup",
    );
  }, [canBookShortNotice, pickupWillCall, transportDate, primaryTime, timeType]);

  const lookup = usePatientLookupState();
  const allSavedPickups =
    lookup.status === "found" && lookup.patient ? lookup.patient.savedPickups : [];
  const savedPickups = useMemo(
    () =>
      allSavedPickups.filter(
        (s) =>
          normalizeText(s.address) !== normalizeText(pickupAddress) ||
          normalizeText(s.cityZip) !== normalizeText(pickupCityZip),
      ),
    [allSavedPickups, pickupAddress, pickupCityZip],
  );

  const allSavedContactPhones =
    lookup.status === "found" && lookup.patient
      ? lookup.patient.savedContactPhones
      : [];
  const savedContactPhones = useMemo(
    () =>
      allSavedContactPhones.filter(
        (s) => digitsOnly(s.phone) !== digitsOnly(contactPhoneWatch),
      ),
    [allSavedContactPhones, contactPhoneWatch],
  );

  const renderSavedPickup = useCallback(
    (s: SavedPickup) => ({
      primary: s.address,
      meta: [s.cityZip, s.location].filter(Boolean).join(" · "),
    }),
    [],
  );

  const onSelectSavedPickup = useCallback(
    (s: SavedPickup) => {
      setValue("pickupAddress", s.address, { shouldDirty: true, shouldTouch: true });
      if (s.cityZip) setValue("pickupCityZip", s.cityZip, { shouldDirty: true });
      if (s.location) setValue("pickupLocation", s.location, { shouldDirty: true });
      setValue(
        "pickupPlace",
        {
          lat: s.lat,
          lon: s.lon,
          buildingNumber: "",
          line1: "",
          city: "",
          state: "",
          postalCode: "",
          formatted: s.address,
        },
        { shouldDirty: true },
      );
      void trigger(["pickupAddress", "pickupCityZip"]);
    },
    [setValue, trigger],
  );

  const renderSavedPhone = useCallback(
    (s: SavedPhone) => ({ primary: formatUsPhone(s.phone) }),
    [],
  );

  const onSelectSavedContactPhone = useCallback(
    (s: SavedPhone) => {
      setValue("contactPhone", formatUsPhone(s.phone), {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue],
  );

  // We have exact map coordinates when the address came from the Google
  // dropdown or a curated Mount Sinai location → show a quiet "Location
  // confirmed". When we don't, a non-blocking hint nudges staff to pick from
  // the dropdown (the server backfills coordinates as a safety net, so it
  // never blocks the booking). The two states are mutually exclusive.
  const hasPickupCoords = Boolean(
    pickupPlace &&
      pickupPlace.lat != null &&
      pickupPlace.lon != null &&
      Number.isFinite(pickupPlace.lat) &&
      Number.isFinite(pickupPlace.lon),
  );
  const hasPickupAddress = pickupAddress.trim().length > 0;

  async function handleContinue() {
    // The short-notice reason is asked (and required) only when the booker may
    // book inside the minimum and the time is inside it.
    const ok = await trigger(["pickupAddress", "contactPhone", "primaryTime", "shortNoticeReason"]);
    if (!ok) return;
    onContinue();
  }

  if (status === "complete") {
    const summary = joinAddressLine(pickupAddress, pickupCityZip) || "—";
    return (
      <WizardCard
        index={index}
        title="Pick-up"
        status="complete"
        summary={summary}
        onEdit={onEdit}
      />
    );
  }

  return (
    <WizardCard
      index={index}
      title="Pick-up"
      description="Where we collect the patient, their contact number, and the pickup time."
      status={status}
    >
      <div className="flex flex-col gap-1.5">
        <Field
          label="Pick-up address"
          required
          htmlFor="pickupAddress"
          error={errors.pickupAddress?.message}
        >
          <Controller
            control={control}
            name="pickupAddress"
            render={({ field }) => (
              <AddressField
                value={field.value ?? ""}
                onChange={field.onChange}
                onPlace={(place, cityZip) => {
                  // Show the full canonical address the user picked (street +
                  // apt + city + state + ZIP) — identical in the field, the
                  // review screen, and dispatch. cityZip is still captured for
                  // the wire; coordinates are what make the trip dispatch.
                  field.onChange(fullAddressFromPlace(place, field.value ?? ""));
                  setValue("pickupPlace", place, { shouldDirty: true });
                  if (cityZip) setValue("pickupCityZip", cityZip, { shouldDirty: true });
                  void trigger(["pickupAddress"]);
                }}
                onClearPlace={() => {
                  setValue("pickupPlace", null, { shouldDirty: true });
                  setValue("pickupCityZip", "", { shouldDirty: true });
                  setValue("pickupLocation", "", { shouldDirty: true });
                }}
                placeholder="Start typing address…"
                maxLength={200}
                invalid={Boolean(errors.pickupAddress)}
              />
            )}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-1.5">
          <LocationsPicker
            mode="pickup"
            onSelect={(picked) => {
              const loc = picked.location;
              setValue("pickupAddress", picked.address || picked.label, {
                shouldDirty: true,
                shouldTouch: true,
              });
              setValue("pickupCityZip", loc.cityZip, { shouldDirty: true });
              setValue("pickupLocation", picked.label, { shouldDirty: true });
              setValue(
                "pickupPlace",
                {
                  lat: loc.lat,
                  lon: loc.lon,
                  buildingNumber: "",
                  line1: loc.addressLine,
                  city: "",
                  state: "",
                  postalCode: "",
                  formatted: picked.address || picked.label,
                },
                { shouldDirty: true },
              );
              // Clear any stale "City, State ZIP is required" error now
              // that we have an address (+ coordinates) for it.
              void trigger(["pickupAddress", "pickupCityZip"]);
            }}
          />
          {savedPickups.length > 0 ? (
            <SavedValuePicker
              label="Saved pick-ups"
              items={savedPickups}
              renderItem={renderSavedPickup}
              onSelect={onSelectSavedPickup}
              description="Click a place to fill the pick-up address fields."
            />
          ) : null}
        </div>
      </div>

      {hasPickupAddress ? (
        hasPickupCoords ? <AddressConfirmedHint /> : <AddressPickHint />
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Field
          className="max-w-md"
          label="Patient contact number"
          required
          htmlFor="contactPhone"
          error={errors.contactPhone?.message}
          hint="Best number where the patient can be reached."
        >
          <Controller
            control={control}
            name="contactPhone"
            render={({ field, fieldState }) => (
              <PhoneField
                id="contactPhone"
                value={field.value ?? ""}
                onChange={field.onChange}
                invalid={Boolean(fieldState.error)}
              />
            )}
          />
        </Field>
        {savedContactPhones.length > 0 ? (
          <SavedValuePicker
            label="Saved phones"
            items={savedContactPhones}
            renderItem={renderSavedPhone}
            onSelect={onSelectSavedContactPhone}
          />
        ) : null}
      </div>

      {/* ─── Companions ───────────────────────────────────────────────
          Whether anyone is travelling with the patient, and how many.
          Dispatch + drivers need this to size the vehicle (a wheelchair
          van seats fewer companions than a sedan). The picked value is
          folded into pickupNotes at wire time as a "Companions: X" line
          so triplink shows it inline with the rest of the pickup
          context — no contract change needed. */}
      <Field
        label="Anyone traveling with the patient?"
        htmlFor="companionCount"
        hint="So dispatch can size the vehicle correctly."
      >
        <Controller
          control={control}
          name="companionCount"
          render={({ field }) => (
            <ChipRadioGroup
              ariaLabel="Number of companions traveling with the patient"
              size="sm"
              value={field.value ?? "0"}
              onValueChange={field.onChange}
              className="justify-start"
              options={[
                { value: "0", label: "Just patient" },
                { value: "1", label: "+1" },
                { value: "2", label: "+2" },
                { value: "3", label: "+3" },
              ]}
            />
          )}
        />
      </Field>

      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Timing
        </span>
        <Controller
          control={control}
          name="pickupWillCall"
          render={({ field }) => (
            <WillCallToggle
              checked={field.value ?? false}
              onCheckedChange={(c) => {
                field.onChange(c);
                if (c) setValue("primaryTime", "");
              }}
            />
          )}
        />
      </div>

      {pickupWillCall ? (
        <p className="rounded-xl border border-dashed border-primary/40 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary">
          Will Call — patient will call when ready for pickup.
        </p>
      ) : (
        <div className="space-y-2">
          <Controller
            control={control}
            name="timeType"
            render={({ field }) => (
              <ChipRadioGroup
                ariaLabel="Time type"
                size="sm"
                value={field.value}
                onValueChange={field.onChange}
                className="justify-start"
                options={[
                  { value: "pickup", label: "Pickup Time" },
                  { value: "appointment", label: "Appointment Time" },
                ]}
              />
            )}
          />
          <Field
            className="max-w-xs"
            label={timeType === "appointment" ? "Appointment Time" : "Pickup Time"}
            required
            htmlFor="primaryTime"
            error={errors.primaryTime?.message}
          >
            <Controller
              control={control}
              name="primaryTime"
              render={({ field, fieldState }) => (
                <TimeField
                  id="primaryTime"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  invalid={Boolean(fieldState.error)}
                />
              )}
            />
          </Field>
          {showAppointmentBuffer ? (
            <AppointmentBufferCallout
              appointmentDisplay={formatTimeForDisplay(primaryTime ?? "")}
              arrivalDisplay={arrivalTimeHint}
              bufferMinutes={APPOINTMENT_BUFFER_MINUTES}
            />
          ) : null}
          {/* Only for someone allowed to book inside the minimum, and only
              once the time they picked actually is. Everyone else just sees
              the "earliest we can take is …" error on the time field. */}
          {showShortNoticeReason ? (
            <Field
              label="Reason for short notice"
              required
              htmlFor="shortNoticeReason"
              error={errors.shortNoticeReason?.message}
              hint="Recorded with the booking so dispatch knows why it came in late."
            >
              <Controller
                control={control}
                name="shortNoticeReason"
                render={({ field, fieldState }) => (
                  <Input
                    id="shortNoticeReason"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="e.g. urgent discharge, patient already waiting"
                    maxLength={200}
                    invalid={Boolean(fieldState.error)}
                  />
                )}
              />
            </Field>
          ) : null}
        </div>
      )}

      <Field label="Pick-up notes (optional)" htmlFor="pickupNotes">
        <Textarea
          id="pickupNotes"
          rows={2}
          placeholder="Gate code, floor, elevator notes, contact instructions…"
          maxLength={1000}
          {...register("pickupNotes")}
        />
      </Field>

      <div className="flex justify-end pt-1">
        <Button type="button" onClick={handleContinue} className="min-w-[10rem]">
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </WizardCard>
  );
}

interface AppointmentBufferCalloutProps {
  appointmentDisplay: string;
  arrivalDisplay: string;
  bufferMinutes: number;
}

function AppointmentBufferCallout({
  appointmentDisplay,
  arrivalDisplay,
  bufferMinutes,
}: AppointmentBufferCalloutProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-start gap-3 rounded-2xl border border-primary/45 bg-primary/10 px-3 py-2.5"
    >
      <Clock4 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
      <div className="flex flex-col gap-0.5 text-sm">
        <p className="font-bold text-primary">
          Pickup will be scheduled {bufferMinutes} min early
        </p>
        <p className="text-foreground">
          Appointment <strong className="font-semibold">{appointmentDisplay}</strong>
          {" — patient on campus by "}
          <strong className="font-semibold">{arrivalDisplay}</strong>.
        </p>
        <p className="text-xs text-muted-foreground">
          Dispatch automatically accounts for the {bufferMinutes}-minute buffer; you don't
          need to subtract it manually.
        </p>
      </div>
    </div>
  );
}

function normalizeText(value: string | null | undefined): string {
  return String(value ?? "").trim().toLowerCase();
}

function digitsOnly(value: string | null | undefined): string {
  return String(value ?? "").replace(/\D/gu, "");
}
